import React from 'react';
import { motion } from 'framer-motion';
import ServiceCard from '../components/ServiceCard';

// Icons
const CrmIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v1h-3zM4.75 12.094A5.973 5.973 0 004 15v1H1v-1a3 3 0 013.75-2.906z"></path>
  </svg>
);

const TransactionIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"></path>
  </svg>
);

const SocialIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v1h-3zM4.75 12.094A5.973 5.973 0 004 15v1H1v-1a3 3 0 013.75-2.906z"></path>
  </svg>
);

const EmailIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
  </svg>
);

const LeadIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
  </svg>
);

const DataIcon = () => (
  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12v3c0 1.657 3.134 3 7 3s7-1.343 7-3v-3c0 1.657-3.134 3-7 3s-7-1.343-7-3z"></path>
    <path d="M3 7v3c0 1.657 3.134 3 7 3s7-1.343 7-3V7c0 1.657-3.134 3-7 3S3 8.657 3 7z"></path>
    <path d="M17 5c0 1.657-3.134 3-7 3S3 6.657 3 5s3.134-3 7-3 7 1.343 7 3z"></path>
  </svg>
);

const Services = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const services = [
    {
      icon: <CrmIcon />,
      title: "CRM Management",
      description: "Update leads, segment lists, automate follow-ups and keep your customer database organized.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978"
    },
    {
      icon: <TransactionIcon />,
      title: "Transaction Coordination",
      description: "Prepare closing documents, schedule inspections, handle MLS updates and paperwork.",
      image: "https://images.unsplash.com/photo-1516315720917-231ef9acce48"
    },
    {
      icon: <SocialIcon />,
      title: "Social Media Management",
      description: "Content creation, post scheduling, engagement tracking and audience growth.",
      image: "https://images.unsplash.com/photo-1573164574230-db1d5e960238"
    },
    {
      icon: <EmailIcon />,
      title: "Email & Calendar Management",
      description: "Inbox organization, meeting scheduling, reminder setup and priority flagging.",
      image: "https://images.unsplash.com/photo-1523398845774-0d176ba6fd41"
    },
    {
      icon: <LeadIcon />,
      title: "Lead Research & Enrichment",
      description: "Find prospects, verify contact details, build and maintain CRM entries.",
      image: "https://images.unsplash.com/photo-1544717297-fa95b6ee9643"
    },
    {
      icon: <DataIcon />,
      title: "Data Entry & Reporting",
      description: "Monthly reports, spreadsheet cleanup, KPI tracking and data visualization.",
      image: "https://images.pexels.com/photos/8532850/pexels-photo-8532850.jpeg"
    }
  ];

  return (
    <section id="services" className="section bg-gradient-secondary">
      <div className="container">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-primary-700 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            What Can Your VA Do?
          </motion.h2>
          <motion.p 
            className="text-neutral-600 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Our VAs are pre-trained in a wide range of administrative tasks to help you reclaim your time.
          </motion.p>
        </div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {services.map((service, index) => (
            <motion.div key={index} variants={cardVariants}>
              <ServiceCard
                icon={service.icon}
                title={service.title}
                description={service.description}
                image={service.image}
              />
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center mt-12">
          <motion.a
            href="#more-services"
            className="btn btn-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            viewport={{ once: true }}
          >
            See More Services
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default Services;